/**
 * DCM-362: human sentences for a form-change diff value. Objects become
 * "value – value" items (in the key order parents recognise: type, name,
 * when to apply, amount), arrays of objects become items joined by "; ".
 *
 * refresh 3.1 (design.md 6): the SENTENCE shape for one diff entry — the
 * label is resolved by the caller (diffLabel in my-forms.service owns the
 * section titles; the shared kit must not import from routes).
 */
const VALUE_KEY_ORDER = ['type', 'name', 'when_to_apply', 'amount'];

/** One entry of a form-version diff (the FormVersionDiff wire shape). */
export interface ChangeEntry {
  readonly path: string;
  /** Answer entries carry the question text; section entries omit it. */
  readonly label?: string;
  readonly previous: unknown;
  readonly next: unknown;
  readonly change: 'added' | 'removed' | 'changed';
}

/** A diff entry as one plain sentence: "{label} added: {value}" etc. */
export function formatChange(d: ChangeEntry, label: string): string {
  const before = formatChangeValue(d.previous);
  const after = formatChangeValue(d.next);
  switch (d.change) {
    case 'added':
      return `${label} added: ${after}`;
    case 'removed':
      return `${label} removed: ${before}`;
    default:
      return `${label}: ${before} → ${after}`;
  }
}

export function formatChangeValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return formatObjectValue(v);
  return String(v);
}

function hasText(v: unknown): boolean {
  return v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0);
}

function orderedKeys(o: Record<string, unknown>): string[] {
  const keys = Object.keys(o);
  const head = VALUE_KEY_ORDER.filter((k) => keys.includes(k) && hasText(o[k]));
  const rest = keys.filter((k) => !VALUE_KEY_ORDER.includes(k) && hasText(o[k]));
  return [...head, ...rest];
}

function raw(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return formatObjectValue(v);
  return String(v);
}

function formatObjectValue(v: object): string {
  const items = Array.isArray(v) ? v : [v];
  return items
    .map((item) => {
      if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
        return orderedKeys(item as Record<string, unknown>)
          .map((k) => raw((item as Record<string, unknown>)[k]))
          .join(' – ');
      }
      return raw(item);
    })
    .join('; ');
}